<template>
    <div :class="{'d-inline-block': small, 'mr-20': small, 'ml-20': small}">
        <modal :name="modalName">
            <div class="p-30" v-if="!submitSuccess && !submitError">

                <div class="h4m text-center">
                    <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" style="display: inline; fill: #0094ab;">
                        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"></path>
                    </svg>
                    <span class="d-inline-block">
                        <slot>{{ defaultModalTitle }}</slot>
                    </span>
                </div>
                <div class="error" style="display: none;"></div>
                <form class="form">
                    <div class="pb-20"></div>
                    <div class="row">
                        <div class="col-6-sm">
                            <input-text
                                input-type="text"
                                name="firstname"
                                v-model="firstname"
                                label="First Name"
                                placeholder="First Name"
                                :validation-state="v$.firstname"
                            ></input-text>
                        </div>
                        <div class="col-6-sm">
                            <input-text
                                input-type="text"
                                name="lastname"
                                v-model="lastname"
                                label="Last Name"
                                placeholder="Last Name"
                                :validation-state="v$.lastname"
                            ></input-text>
                        </div>
                    </div>
                    <input-text
                        input-type="text"
                        name="email"
                        v-model="email"
                        label="Email"
                        placeholder="Email"
                        :validation-state="v$.email"
                    ></input-text>
                    <input-text
                        input-type="text"
                        name="telephone"
                        v-model="telephone"
                        label="Telephone"
                        placeholder="Telephone"
                        :validation-state="v$.telephone"
                    ></input-text>

                    <vue-address :large="true" v-model="address" ref="address" :email="email" :validation-state="v$.address"></vue-address>

                    <div class="pb-20"></div>

                    <checkbox
                        name="privacy"
                        form-value="yes"
                        v-model="privacy"
                        :validation-state="v$.privacy"
                    >
                        By proceeding, I agree that Regit.cars and its automotive partners may contact me by email,
                        telephone or text message with information about other automotive services I agree for my
                        personal data to be used in accordance with the <a href="/privacy-policy" target="_blank">Privacy
                        Policy</a> and to the Terms and Conditions of this website.<br>
                    </checkbox>

                    <div class="row">
                        <div class="col-6 text-left">
                            <button type="button" class="button button--medium button--ghost" @click="hide">Close</button>
                        </div>
                        <div class="col-6 text-right">
                            <button type="button" class="button button--medium" @click="submitEnquiry">Submit
                                Request
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <div class="p-30" v-if="submitSuccess">
                <p>
                    Thank you for your interest in a {{ make }} {{ model }}.<br>
                    <strong>Someone from the regit.cars team will be in touch very shortly.</strong>
                </p>
                <button class="button button--medium button--hover" @click="hide">Close</button>
            </div>

            <div class="p-30" v-if="submitError">
                <p>
                    A generic error happened, please contact the site administration.
                </p>
                <button class="button button--medium button--hover" @click="hide">Close</button>
            </div>
        </modal>
        <button :class="buttonClasses" @click="show">
            {{ label }}
            <span class="button__subtitle" v-if="subtitle !== ''"><br>{{ subtitle }}</span>
        </button>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import Address from './address.vue';
import axiosClient from '../../modules/axiosClient';
import sendAnalyticsEvent from '../../modules/sendAnalyticsEvent';
import Checkbox from './checkbox.vue';
import useVuelidate from '@vuelidate/core';
import {required, email, maxLength, sameAs, helpers} from '@vuelidate/validators';
import {phone} from '../../modules/regitValidators';

const allowedTypes = [
    'testdrive',
    'brochure',
    'quote',
    'generic',
];
const autoincrement = (function() {
    let i = 1;

    return function() {
        return i++;
    };
})();

export default defineComponent({
    props: {
        'make': {type: String, default: ''},
        'model': {type: String, default: ''},
        'type': {
            type: String,
            validator: (value: string) => {
                return allowedTypes.indexOf(value) !== -1;
            },
        },
        'label': {type: String},
        'small': {type: Boolean, default: false},
        'subtitle': {type: String, default: ''},
        'pageSource': {type: String, default: null},
    },
    components: {
        'vue-address': Address,
        'checkbox': Checkbox,
    },
    data() {
        return {
            submitSuccess: false,
            submitError: false,
            uuid: '0',

            // form data
            firstname: '',
            lastname: '',
            email: '',
            telephone: '',
            address: {
                postcode: '',
                address1: '',
                address2: '',
                address3: '',
                town: '',
                county: '',
            },
            privacy: false,
        };
    },
    computed: {
        buttonClasses(): VueClassMapping {
            return {
                'button': true,
                'button--small': this.small,
                'button--medium': !this.small,
                'button--ghost': this.small,
                'button--hover': !this.small,
                'button--full': !this.small,
                'button--with-subtitle': this.subtitle !== '',
            };
        },
        currentType(): string {
            // needed for legacy news buttons
            let typeFromLabel = this.label || '';
            typeFromLabel = typeFromLabel.toLowerCase().replace(' ', '');
            if (typeof this.type === 'undefined' && allowedTypes.indexOf(typeFromLabel) !== -1) {
                return typeFromLabel;
            }

            return this.type || '';
        },
        modalName(): string {
            return `enquire-form-${this.currentType}-${this.uuid}`;
        },
        defaultModalTitle(): string {
            switch (this.currentType) {
                case 'brochure':
                    return `Order a ${this.make} ${this.model} brochure`;
                case 'testdrive':
                    return `Request a ${this.make} ${this.model} test drive`;
                case 'quote':
                    return `Request a quote for a ${this.make} ${this.model} quote`;
                default:
                    return `Keep informed about ${this.make} ${this.model}`;
            }
        },
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    validations() {
        return {
            firstname: {
                required: helpers.withMessage('Please enter your name', required),
            },
            lastname: {
                required: helpers.withMessage('Please enter your surname', required),
            },
            telephone: {
                required: helpers.withMessage('Please enter your telephone', required),
                phone: helpers.withMessage('Please enter a valid telephone', phone),
            },
            email: {
                required: helpers.withMessage('Please enter your email', required),
                email,
            },
            privacy: {
                sameAs: helpers.withMessage('Please accept our T&C', sameAs(true)),
            },
            address: {
                postcode: {
                    required: helpers.withMessage('Please enter your postcode', required),
                    maxLengthValue: maxLength(10),
                },
                address1: {
                    required,
                    maxLengthValue: maxLength(255),
                },
                address2: {
                    maxLengthValue: maxLength(255),
                },
                address3: {
                    maxLengthValue: maxLength(255),
                },
                town: {
                    required: helpers.withMessage('Please enter your town', required),
                    maxLengthValue: maxLength(255),
                },
                county: {
                    maxLengthValue: maxLength(255),
                },
            },
        };
    },
    methods: {
        async submitEnquiry() {
            const validationPassed = await this.v$.$validate();
            if (validationPassed) {
                this.submitRequest();
            }
        },
        submitRequest() {
            const payload = {
                'manufacturer': this.make,
                'model': this.model,
                'affiliate': 'regit',
                'type': this.currentType,
                'firstname': this.firstname,
                'surname': this.lastname,
                'tel': this.telephone,
                'email': this.email,
                'postcode': this.address.postcode,
                'address1': this.address.address1,
                'address2': this.address.address2,
                'address3': this.address.address3,
                'town': this.address.town,
                'county': this.address.county,
                'pageSource': this.pageSource,
            };

            axiosClient.post('/enquire/embedded/process', payload).then((response) => {
                if (response.status === 201) {
                    this.submitSuccess = true;
                    sendAnalyticsEvent('Conversion Tracking', 'Lead Generation', 'Lead - ' + this.currentType);
                } else {
                    this.submitError = true;
                }
            }, (response) => {
                this.submitError = true;
                console.log('API call error: ', response);
            });
        },
        show() {
            sendAnalyticsEvent('Conversion Tracking', 'Lead Generation', 'Display form - ' + this.currentType);
            this.$modal.show(this.modalName);
        },
        hide() {
            this.$modal.hide(this.modalName);
        },
    },
    mounted() {
        this.uuid = autoincrement().toString();
    },
});
</script>
